"use client"

import React, { useState, useRef, useEffect, useCallback } from "react"
import { X, Plus, Trash2, GripVertical, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/use-translation"
import { useToast } from "@/hooks/use-toast"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ChecklistItem } from "@/types/workflow"

interface ChecklistPanelProps {
  isOpen: boolean
  onClose: () => void
  itemId: string
  workOrderId: string
  fieldConfigurationId: string
  itemTitle?: string
  columnTitle?: string
  initialItems?: ChecklistItem[]
  onSave?: (items: ChecklistItem[]) => Promise<void>
  canEdit?: boolean
}

function SortableChecklistItem({
  item,
  index,
  onToggle,
  onTitleChange,
  onDelete,
  onEdit,
  isEditing,
  canEdit,
}: {
  item: ChecklistItem
  index: number
  onToggle: (index: number) => void
  onTitleChange: (index: number, title: string) => void
  onDelete: (index: number) => void
  onEdit: (index: number) => void
  isEditing: boolean
  canEdit: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `item-${index}` })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
    >
      {canEdit && (
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
      )}
      
      <Checkbox
        id={`checklist-item-${index}`}
        checked={item.isCompleted}
        onCheckedChange={() => canEdit && onToggle(index)}
        disabled={!canEdit}
        className="flex-shrink-0"
      />
      
      {isEditing && canEdit ? (
        <Input
          value={item.title}
          onChange={(e) => onTitleChange(index, e.target.value)}
          onBlur={() => onEdit(-1)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEdit(-1)
            }
            if (e.key === 'Escape') {
              onEdit(-1)
            }
          }}
          className="flex-1"
          placeholder="Checklist item..."
          autoFocus
        />
      ) : (
        <div
          onClick={() => canEdit && onEdit(index)}
          className={`flex-1 text-sm ${canEdit ? 'cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1' : 'cursor-default'} ${
            item.isCompleted ? 'text-gray-400' : 'text-gray-900'
          }`}
        >
          {item.title}
        </div>
      )}
      
      {canEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onDelete(index)}
          disabled={item.isCompleted}
          className={`flex-shrink-0 ${item.isCompleted ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600'}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}

export function ChecklistPanel({
  isOpen,
  onClose,
  workOrderId,
  itemTitle,
  columnTitle,
  initialItems = [],
  onSave,
  canEdit = true,
}: ChecklistPanelProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [items, setItems] = useState<ChecklistItem[]>(initialItems)
  const [isVisible, setIsVisible] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [newItemText, setNewItemText] = useState("")
  const [editingItemIndex, setEditingItemIndex] = useState<number>(-1)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [duplicateError, setDuplicateError] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setItems(initialItems)
      setHasUnsavedChanges(false)
      setShowCloseConfirmation(false)
    }
  }, [isOpen, initialItems])

  const handleSave = useCallback(async () => {
    // Validate for duplicate titles before saving
    const activeItems = items.filter(item => !item.isDeleted)
    const titles = activeItems.map(item => item.title.trim().toLowerCase())
    const hasDuplicates = titles.some((title, index) => titles.indexOf(title) !== index)
    
    if (hasDuplicates) {
      toast({
        variant: "destructive",
        title: t('error') || "Error",
        description: t('duplicateItemsPreventSave') || "Cannot save: duplicate checklist items found",
      })
      return
    }
    
    if (onSave) {
      setIsSaving(true)
      try {
        await onSave(items)
        setHasUnsavedChanges(false)
        onClose()
      } catch (error) {
        console.error('Failed to save checklist:', error)
      } finally {
        setIsSaving(false)
      }
    } else {
      onClose()
    }
  }, [onSave, items, onClose, toast, t])

  const handleCancel = useCallback(() => {
    if (canEdit && hasUnsavedChanges) {
      setShowCloseConfirmation(true)
    } else {
      onClose()
    }
  }, [canEdit, hasUnsavedChanges, onClose])

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmation(false)
    setHasUnsavedChanges(false)
    onClose()
  }, [onClose])

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        handleCancel()
      }
    }

    if (isOpen && !showCloseConfirmation) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleCancel, showCloseConfirmation])

  const handleAddItem = () => {
    if (!newItemText.trim()) return

    const trimmedTitle = newItemText.trim()
    const activeItems = items.filter(item => !item.isDeleted)
    
    // Check for duplicate titles
    const isDuplicate = activeItems.some(item => 
      item.title.toLowerCase() === trimmedTitle.toLowerCase()
    )
    
    if (isDuplicate) {
      setDuplicateError(true)
      return
    }

    const newItem: ChecklistItem = {
      title: trimmedTitle,
      isCompleted: false,
      order: activeItems.length,
      isDeleted: false,
    }

    setItems([...items, newItem])
    setNewItemText("")
    setDuplicateError(false)
    setHasUnsavedChanges(true)
  }

  const handleToggleItem = (index: number) => {
    setItems(items.map((item, i) => 
      i === index 
        ? { ...item, isCompleted: !item.isCompleted }
        : item
    ))
    setHasUnsavedChanges(true)
  }

  const handleTitleChange = (index: number, title: string) => {
    const trimmedTitle = title.trim()
    
    // Only validate on blur/enter (when title is finalized)
    // For now, allow typing but validate on save
    setItems(items.map((item, i) => 
      i === index 
        ? { ...item, title }
        : item
    ))
    setHasUnsavedChanges(true)
  }

  const handleDeleteItem = (index: number) => {
    setItems(items.map((item, i) =>
      i === index
        ? { ...item, isDeleted: true }
        : item
    ))
    setHasUnsavedChanges(true)
  }

  const handleEditItem = (index: number) => {
    // When closing edit mode, validate for duplicates
    if (index === -1 && editingItemIndex !== -1) {
      const editedItem = items[editingItemIndex]
      if (editedItem && !editedItem.isDeleted) {
        const trimmedTitle = editedItem.title.trim()
        const activeItems = items.filter((item, i) => !item.isDeleted && i !== editingItemIndex)
        
        const isDuplicate = activeItems.some(item => 
          item.title.toLowerCase() === trimmedTitle.toLowerCase()
        )
        
        if (isDuplicate) {
          // Revert to previous title or keep it for now
          // User will be prevented from saving
          setEditingItemIndex(-1)
          return
        }
      }
    }
    
    setEditingItemIndex(index === editingItemIndex ? -1 : index)
  }

  const handleDragEnd = (event: any) => {
    if (!canEdit) return;
    
    const { active, over } = event
    if (active.id !== over?.id) {
      // Extract indices from "item-X" format
      const oldIndex = parseInt(active.id.toString().replace('item-', ''))
      const newIndex = parseInt(over.id.toString().replace('item-', ''))
      
      // Get only non-deleted items
      const nonDeletedItems = items.filter(item => !item.isDeleted)
      const deletedItems = items.filter(item => item.isDeleted)
      
      // Reorder and update order field
      const reorderedActiveItems = arrayMove(nonDeletedItems, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index,
      }))
      
      // Combine reordered active items with deleted items
      setItems([...reorderedActiveItems, ...deletedItems])
      setHasUnsavedChanges(true)
    }
  }

  const activeItems = items.filter(item => !item.isDeleted)
  const completedCount = activeItems.filter(item => item.isCompleted).length
  const totalCount = activeItems.length

  if (!isOpen) {
    return null
  }

  return (
    <div 
      ref={panelRef} 
      className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col font-roboto transition-all duration-300 ease-out"
      style={{
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        opacity: isVisible ? 1 : 0
      }}
    >
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className="text-lg font-semibold text-gray-900">
            {columnTitle || 'Checklist'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-8 w-8 p-0"
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-4 pb-4">
          <div className="text-sm text-gray-600">
            {t('worker')}: <span className="font-medium text-gray-900">{itemTitle || 'Unknown'}</span> | {t('workOrderIdLabel')}: <span className="font-medium text-gray-900">{workOrderId}</span>
          </div>
          <div className="text-sm text-gray-500 mt-3">
            {completedCount} {t('of')} {totalCount} {t('itemsCompleted')}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-4">
            {/* Checklist items */}
            {activeItems.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>{t('noRecordsFound')}</p>
                <p className="text-sm text-gray-400 mt-1">Add a new checklist item below.</p>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={activeItems.map((_, idx) => `item-${idx}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {activeItems.map((item, activeIndex) => {
                      const actualIndex = items.indexOf(item);
                      return (
                        <SortableChecklistItem
                          key={actualIndex}
                          item={item}
                          index={activeIndex}
                          onToggle={(idx) => handleToggleItem(actualIndex)}
                          onTitleChange={(idx, title) => handleTitleChange(actualIndex, title)}
                          onDelete={(idx) => handleDeleteItem(actualIndex)}
                          onEdit={(idx) => handleEditItem(actualIndex)}
                          isEditing={editingItemIndex === actualIndex}
                          canEdit={canEdit}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Add new item - moved to bottom */}
      {canEdit && (
        <div className="p-4 border-t border-gray-200">
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={newItemText}
                onChange={(e) => {
                  setNewItemText(e.target.value)
                  setDuplicateError(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddItem()
                  }
                }}
                placeholder={t('addNewItem')}
                className={`flex-1 ${duplicateError ? 'border-red-500 focus:ring-red-500' : ''}`}
              />
              <Button
                onClick={handleAddItem}
                disabled={!newItemText.trim()}
                className="flex-shrink-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {duplicateError && (
              <p className="text-xs text-red-600">
                {t('duplicateItemError')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex justify-end items-center gap-2">
          {canEdit ? (
            <>
              <Button
                onClick={handleCancel}
                disabled={isSaving}
                variant="elliptical-outline"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                variant="elliptical"
              >
                {isSaving ? t('loading') : t('save')}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleCancel}
              variant="elliptical"
            >
              {t('close')}
            </Button>
          )}
        </div>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      {showCloseConfirmation && (
        <Dialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>{t('unsavedChecklistChanges')} {columnTitle || 'Checklist'}</DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm text-gray-600 mb-4">
              {t('unsavedChecklistMessage')}
            </DialogDescription>
            <div className="flex justify-end gap-2">
              <Button variant="elliptical-outline" onClick={handleCancelClose}>
                {t('cancel')}
              </Button>
              <Button variant="elliptical" onClick={handleConfirmClose}>
                {t('close')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

